import { API, APIObject } from '@/shared/plugins/Api/API'
import _omit from 'lodash/omit'
import _set from 'lodash/set'

/*
 * These four operators take no value.
 * NOTE: the same list is in `NO_VALUE_OPERATORS` in
 * lakehouse/components/RowFilters/condition.js. It is copied here because the
 * API layer must not import from a module. Change both together.
 */
const NO_OPERAND_OPERATORS = ['is empty', 'is not empty', 'is null', 'is not null']

/*
 * The builder leaves an empty string on the operators that take no value, which
 * the backend rejects, so it is sent as null. Every route with a predicate
 * calls this. Nothing is changed in place, so the wizard keeps the value it
 * holds.
 */
function normalizeRowFilter (rowFilter) {
  const children = rowFilter?.builder?.children
  if (!Array.isArray(children)) return rowFilter
  return {
    ...rowFilter,
    builder: {
      ...rowFilter.builder,
      children: children.map(condition => (
        NO_OPERAND_OPERATORS.includes(condition?.operator)
          ? { ...condition, value: null }
          : condition
      ))
    }
  }
}

/*
 * One row-filter rule. Create goes through the table the rule guards: the
 * backend reads that table from the URL to check the caller's rights. So
 * `dataset_id` and `table_id` must be set on the object before create().
 * Every other write only needs the rule id.
 */
class RowFilter extends APIObject {
  constructor (options) {
    // Init
    super('DM', options)

    // Default variables
    this.rule_name = this.rule_name || ''
    this.principal = this.principal || []
    this.dataset_id = this.dataset_id || ''
    this.table_id = this.table_id || ''
    this.row_filter = this.row_filter || {}
    this.active = this.active || false
  }

  // Used by every write on a rule that already exists. It does the same as
  // the nested `.../row-filters/${this._id}` route, but without the table.
  get _ruleUrl () {
    return `v4/rls-rules/${this._id}`
  }

  // Only the backend fills these in, so they never go back on the wire.
  // `_id` is kept: PUT carries it in the body as well as in the URL.
  // A body with no predicate is left alone: this also runs on the save queue,
  // which holds the changed fields alone.
  _filter (object) {
    const filtered = _omit(super._filter(object), [
      'catalog',
      'schema',
      'table',
      'created_at',
      'updated_at',
      'created_by',
      'updated_by'
    ])
    if (!filtered.row_filter) return filtered
    return { ...filtered, row_filter: normalizeRowFilter(filtered.row_filter) }
  }

  // The only write that goes through the table. `dataset_id` holds the
  // database: the field is named after the dataset, but that is what it is.
  async create () {
    await super.create({
      method: 'POST',
      url: `v4/databases/${this.dataset_id}/tables/${this.table_id}/row-filters`
    })
    return this
  }

  // PUT replaces the whole rule, so the body is the rule as it now stands and
  // not the save queue.
  async save () {
    await super.save({
      method: 'PUT',
      url: this._ruleUrl,
      data: this.toJSON()
    }, true)
    return this
  }

  // PATCH carries the active flag alone; a full edit goes through save().
  // An empty answer leaves the flag we just sent as the truth.
  async setActive (active) {
    await super.save({
      method: 'PATCH',
      url: this._ruleUrl,
      data: { active }
    }, true)
    this.active = active
    return this
  }

  async remove () {
    return this.request({
      method: 'DELETE',
      url: this._ruleUrl
    })
  }
}

class RowFilters extends API {
  /**
  * List all row filters, across every dataset and table of the project
  *
  * @return {Array} RowFilter array
  */
  async list () {
    const rules = await this.request({
      method: 'GET',
      url: 'v4/row-filters'
    })

    return (rules || []).map(rule => {
      try {
        return new RowFilter(rule)
      } catch (err) {
        console.error(err.stack)
        return null
      }
    }).filter(i => i)
  }

  /**
  * Check a filter and count the rows it keeps, without saving anything.
  * The body is the `row_filter` alone, or a `policy_id` for a saved rule.
  * Nothing is created, so it stays here instead of on a RowFilter. The body is
  * not built from a rule, so the predicate is fixed up here.
  *
  * @return {Object} Row counts
  */
  preview (databaseId, tableId, payload) {
    return this.request({
      method: 'POST',
      url: `v4/databases/${databaseId}/tables/${tableId}/row-filters/preview`,
      data: payload?.row_filter
        ? { ...payload, row_filter: normalizeRowFilter(payload.row_filter) }
        : payload
    })
  }

  /**
  * Add new row filter
  *
  * @return {Object} RowFilter item
  */
  new (queryString) {
    const item = {}
    for (const key in queryString) {
      _set(item, key, queryString[key])
    }
    return new RowFilter(item)
  }
}

export default RowFilters
export {
  RowFilter,
  RowFilters
}
